<script setup>
import { onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import Integration from '../Integration.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import Button from 'dashboard/components-next/button/Button.vue';

// Every calendar provider (working or interface-only) is registered the
// same way any other Chatwoot integration is — via config/integration/
// apps.yml + the existing Integrations::Hook system. This page just
// filters that same generic list down to the calendar-tagged entries and
// renders each with the existing Integration.vue connect/disconnect widget,
// so the UI never needs to know Cal.com/Google/Microsoft-specific details.
const CALENDAR_PROVIDER_IDS = ['cal_com', 'google_calendar', 'microsoft_365'];

const store = useStore();
const { t } = useI18n();
const integrationGetter = useMapGetter('integrations/getIntegration');

const integrations = computed(() =>
  CALENDAR_PROVIDER_IDS.map(id => integrationGetter.value(id)).filter(
    integration => integration?.id
  )
);

onMounted(() => {
  store.dispatch('integrations/get');
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <h2 class="mb-0 text-lg font-medium text-n-slate-12">
      {{ t('CALENDAR_INTEGRATIONS.TITLE') }}
    </h2>
    <p class="mt-0 text-sm text-n-slate-11">
      {{ t('CALENDAR_INTEGRATIONS.DESCRIPTION') }}
    </p>
    <div
      v-for="integration in integrations"
      :key="integration.id"
      class="w-full"
    >
      <Integration
        :integration-id="integration.id"
        :integration-name="integration.name"
        :integration-description="integration.description"
        :integration-enabled="integration.enabled"
        :integration-action="
          integration.enabled ? 'disconnect' : integration.action
        "
      >
        <template v-if="!integration.enabled && !integration.action" #action>
          <Label
            :label="t('CALENDAR_INTEGRATIONS.COMING_SOON')"
            color="amber"
            compact
          />
        </template>
        <!--
          Cal.com (and any future schema-driven, non-OAuth calendar
          provider) has no external OAuth redirect — `integration.action`
          is just the manifest's relative path marker, not a browsable
          URL. Route to Chatwoot's own generic integration settings page
          (the same named route the main Settings > Integrations grid
          already uses for OpenAI/Dyte/etc.) instead of treating it as
          an href.
        -->
        <template v-else-if="!integration.enabled" #action>
          <router-link
            :to="{
              name: 'settings_applications_integration',
              params: { integration_id: integration.id },
            }"
          >
            <Button
              faded
              blue
              :label="t('INTEGRATION_SETTINGS.CONNECT.BUTTON_TEXT')"
            />
          </router-link>
        </template>
      </Integration>
    </div>
  </div>
</template>
